"use client";

import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import Image from "next/image";
import Link from "next/link";
import { Offer, ProductData } from "@/services/dataTypes";
import { discardHTMLTags, getBaseImageUrl } from "@/constants/hooks";
import OfferDuration from "./OfferDuration";

interface Props {
    data: Offer | ProductData;
    onClose: () => void;
    domain: string;
    merchantHref: string;
    finalDiscountTag?: string | null;
    merchantImg?: string | null;
    productImg?: string | null;
}

const SimpleOfferModal = ({ data, onClose, domain, merchantHref, finalDiscountTag, merchantImg, productImg }: Props) => {
    const [mounted, setMounted] = useState(false);
    const [visible, setVisible] = useState(false);
    const [copied, setCopied] = useState(false);
    const [imageSrc, setImageSrc] = useState("");
    const [showFullDesc, setShowFullDesc] = useState(false);
    const [codeRevealed, setCodeRevealed] = useState(false);
    const modalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setMounted(true);
        requestAnimationFrame(() => setVisible(true));
        document.body.style.overflow = "hidden";
        const handleEsc = (e: KeyboardEvent) => { if (e.key === "Escape") handleClose(); };
        window.addEventListener("keydown", handleEsc);
        return () => {
            window.removeEventListener("keydown", handleEsc);
            document.body.style.overflow = "unset";
        };
    }, []);

    useEffect(() => {
        if (productImg) setImageSrc(getBaseImageUrl(domain, productImg, ""));
        else if ((data as any)?.offer_type?.name === "product" && (data as ProductData)?.product_image)
            setImageSrc(getBaseImageUrl(domain, (data as ProductData).product_image, ""));
        else if (merchantImg) setImageSrc(getBaseImageUrl(domain, merchantImg, ""));
        else if ((data as Offer).merchant?.merchant_logo)
            setImageSrc(getBaseImageUrl(domain, (data as Offer).merchant.merchant_logo, ""));
    }, [data, domain, merchantImg, productImg]);

    const handleClose = () => {
        setVisible(false);
        setTimeout(onClose, 260);
    };

    const handleCopy = () => {
        if (!data?.coupon_code) return;
        const done = () => { setCopied(true); setTimeout(() => setCopied(false), 2500); };
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(data.coupon_code).then(done).catch(() => fallbackCopy(data.coupon_code!, done));
        } else fallbackCopy(data.coupon_code, done);
    };

    const fallbackCopy = (text: string, cb: () => void) => {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.cssText = "position:fixed;left:-9999px;top:0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        try { document.execCommand("copy"); cb(); } catch { /* silent */ }
        document.body.removeChild(ta);
    };

    if (!mounted) return null;

    const finalUrl = data?.url?.startsWith("/") ? data.url.replace(/^\/+/, "") : data?.url;
    const absoluteOutUrl = `/${finalUrl}`;
    const cleanDesc = discardHTMLTags(data?.offer_detail || "");
    const isLongDesc = cleanDesc.length > 160;
    const hasCoupon = !!data?.coupon_code;

    return ReactDOM.createPortal(
        /* Backdrop */
        <div
            className="fixed inset-0 z-[50000] flex items-end sm:items-center justify-center p-0 sm:p-4"
            style={{
                background: visible ? "rgba(0,0,0,0.45)" : "rgba(0,0,0,0)",
                backdropFilter: visible ? "blur(6px)" : "blur(0px)",
                transition: "background 0.26s ease, backdrop-filter 0.26s ease",
            }}
            onClick={handleClose}
        >
            {/* Modal shell */}
            <div
                ref={modalRef}
                className="relative w-full bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden flex flex-col"
                style={{
                    maxWidth: "400px",
                    maxHeight: "90vh",
                    boxShadow: "0 24px 80px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.06)",
                    transform: visible ? "translateY(0) scale(1)" : "translateY(28px) scale(0.97)",
                    opacity: visible ? 1 : 0,
                    transition: "transform 0.26s cubic-bezier(0.34,1.4,0.64,1), opacity 0.26s ease",
                    fontFamily: "'DM Sans', sans-serif",
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* Green top strip */}
                <div className="h-1 w-full flex-shrink-0" style={{ background: "#5D27A1" }} />

                {/* Close button */}
                <button
                    onClick={handleClose}
                    aria-label="Close modal"
                    className="absolute top-4 right-4 z-30 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-all duration-200 focus:outline-none"
                >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                {/* Scrollable body */}
                <div className="overflow-y-auto flex-grow" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>

                    {/* Hero: image + badges */}
                    <div className="flex flex-col items-center pt-8 pb-5 px-6 border-b border-gray-100">
                        {/* Image */}
                        <div className="relative mb-4">
                            <div className="w-20 h-20 rounded-2xl bg-gray-50 border border-gray-100 overflow-hidden flex items-center justify-center">
                                {imageSrc ? (
                                    <Image src={imageSrc} alt={data?.offer_title || "offer"} width={64} height={64} className="object-contain p-2" />
                                ) : (
                                    <svg className="w-8 h-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 14.25l6-6m4.5-3.493V21.75l-3.75-1.5-3.75 1.5-3.75-1.5-3.75 1.5V4.757c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0c1.1.128 1.907 1.077 1.907 2.185z" />
                                    </svg>
                                )}
                            </div>
                            {/* Discount badge */}
                            {finalDiscountTag && (
                                <span
                                    className="absolute -top-2 -right-3 text-[9px] font-bold px-2 py-0.5 rounded-md text-white"
                                    style={{ background: "#5D27A1" }}
                                >
                                    {finalDiscountTag}
                                </span>
                            )}
                        </div>

                        {/* Status pills */}
                        <div className="flex items-center gap-2 flex-wrap justify-center">
                            <span
                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold"
                                style={{ background: "rgba(74,154,110,0.08)", color: "#5D27A1", border: "1px solid rgba(74,154,110,0.15)" }}
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-[#5D27A1]" />
                                Verified
                            </span>
                            <OfferDuration endDate={data?.end_date} />
                        </div>
                    </div>

                    {/* Offer title */}
                    <div className="px-6 pt-5 pb-4">
                        <h2 className="text-[15px] font-bold text-gray-900 leading-snug text-center m-0">
                            {data?.offer_title}
                        </h2>
                    </div>

                    {/* CTA section */}
                    <div className="px-6 pb-5">
                        {hasCoupon ? (
                            /* Coupon block */
                            <div>
                                {/* Code row */}
                                <div className="flex items-stretch rounded-xl border-2 border-dashed border-gray-200 overflow-hidden mb-2">
                                    {/* Code display */}
                                    <div className="flex-1 flex items-center px-4 py-3 bg-gray-50">
                                        {codeRevealed ? (
                                            <span className="text-sm font-bold text-gray-800 tracking-wider select-all truncate">
                                                {data.coupon_code}
                                            </span>
                                        ) : (
                                            <span className="text-sm font-bold text-gray-400 tracking-wider blur-[4px] select-none pointer-events-none">
                                                {data.coupon_code}
                                            </span>
                                        )}
                                    </div>
                                    {/* Action button */}
                                    <button
                                        onClick={() => { if (!codeRevealed) setCodeRevealed(true); else handleCopy(); }}
                                        className="flex items-center gap-1.5 px-4 py-3 text-[11px] font-semibold text-white transition-all duration-200 focus:outline-none whitespace-nowrap"
                                        style={{ background: copied ? "#471d7c" : "#5D27A1" }}
                                    >
                                        {copied ? (
                                            <>
                                                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                                Copied
                                            </>
                                        ) : codeRevealed ? (
                                            <>
                                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                </svg>
                                                Copy
                                            </>
                                        ) : (
                                            <>
                                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                </svg>
                                                Reveal
                                            </>
                                        )}
                                    </button>
                                </div>

                                <p className="text-[10px] text-center text-gray-400 m-0 mb-4">
                                    {codeRevealed ? "Apply code at checkout" : "Click reveal to show code"}
                                </p>

                                {/* Secondary CTA */}
                                <Link
                                    href={absoluteOutUrl}
                                    target="_blank"
                                    className="w-full flex items-center justify-center py-3 rounded-xl text-sm font-semibold no-underline border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 focus:outline-none"
                                    onClick={handleClose}
                                >
                                    Go to Store
                                </Link>
                            </div>
                        ) : (
                            /* Deal CTA */
                            <Link
                                href={absoluteOutUrl}
                                target="_blank"
                                className="w-full flex items-center justify-center py-3.5 rounded-xl text-sm font-semibold text-white no-underline transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg focus:outline-none"
                                style={{ background: "#5D27A1", boxShadow: "0 4px 16px rgba(74,154,110,0.28)" }}
                                onClick={handleClose}
                            >
                                Get Deal Now
                            </Link>
                        )}
                    </div>

                    {/* Description */}
                    {cleanDesc && (
                        <div className="mx-6 mb-5 px-4 py-4 rounded-xl bg-gray-50 border border-gray-100">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-2 m-0">Offer Details</p>
                            <p className={`text-xs text-gray-600 leading-relaxed m-0 transition-all duration-300 ${!showFullDesc && isLongDesc ? "line-clamp-3" : ""}`}>
                                {cleanDesc}
                            </p>
                            {isLongDesc && (
                                <button
                                    onClick={() => setShowFullDesc(!showFullDesc)}
                                    className="mt-2 text-[10px] font-semibold text-[#5D27A1] hover:text-[#471d7c] transition-colors duration-200 focus:outline-none"
                                >
                                    {showFullDesc ? "Show less" : "Read more"}
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-3.5 border-t border-gray-100 bg-white flex items-center justify-between gap-3 flex-shrink-0">
                    <Link
                        href={merchantHref}
                        className="text-[11px] font-semibold text-[#5D27A1] hover:text-[#471d7c] no-underline transition-colors duration-200"
                        onClick={handleClose}
                    >
                        More from this store
                    </Link>
                    <span className="text-[10px] text-gray-400">
                        {hasCoupon ? "Coupon" : "Deal"}
                    </span>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default SimpleOfferModal;
